
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import LogTable from "@/components/logs/LogTable.vue";
import { Getter } from "vuex-class";
import { getTokenLink } from "@/utils/misc";

const MAX_DISPLAYED_TABS: number = 1;

@Component({
    components: {LogTable},
})
export default class RelayBoxLocation extends Vue {
    @Prop({default: () => []})
    public locations: any[];

    @Prop({default: null})
    public box: any;

    @Prop({default: false})
    public show: boolean;

    @Prop({default: true})
    public loading: boolean;

    @Prop({default: false})
    public hasEditRight: boolean;

    private activeSheet: number = 1;
    private displayRefreshMessage: boolean = false;
    private isEditSizeMode: boolean = false;
    private loadingEditLocationsSize: boolean = false;
    private displayedLocations: any = [];
    private availableSizes: any = [];
    private readonly getTokenLink = getTokenLink;


    @Watch("locations", { immediate: true, deep: true })
    public async handler() {
      this.resetLocations();
    }

    get paginationStyleThreshold(): number {
        return MAX_DISPLAYED_TABS * 4;
    }

    get nextToActiveSheets(): number[] {
        const baseArray: number[] = Array.from(Array(this.box.sheets).keys());
        return baseArray.map((i) => i + 1).filter((i) =>
            i >= this.activeSheet - MAX_DISPLAYED_TABS
            && i <= this.activeSheet + MAX_DISPLAYED_TABS,
        );
    }

    get columnsName(): string[] {
        const columns = [];
        for (let i = 0; i < this.box.columns ; i++) {
            columns.push(this.getColumnName(i));
        }
        return columns;
    }

    get orderedLocations(): any[][][] {
        const locations = [];
        try {
            for (let i = 0; i < this.box.sheets ; i++) {
                locations.push([]);
            }
            for (let i = 0; i < this.box.lines ; i++) {
                locations.map((sheet) => {
                    sheet.push([]);
                });
            }
            this.displayedLocations.forEach((loc) => {
                locations[loc.sheet - 1][loc.line - 1][loc.column - 1] = loc;
            });
        } catch {
            this.displayRefreshMessage = true;
        }
        return locations;
    }

    get connectedBoxProvider(): any {
        return this.box?.ConnectedBox?.provider;
    }

    get canEditLocationsSizes(): any {
        return this.connectedBoxProvider
          && this.availableSizes?.length
          && this.hasEditRight;
    }

    get areLocationsModified(): boolean {
        return this.displayedLocations.some((loc) => loc.edited);
    }

    private async mounted(): Promise<void> {
      await this.fetchAvailableSizes();
    }

    private resetLocations(): void {
      const defaultSize = this.availableSizes.find((size) => size.isDefault);
      this.displayedLocations = this.locations.map((loc) => ({
        ...loc,
        Size: loc.Size || defaultSize,
        edited: !loc.Size,
      }));
    }

    private isLocationEmpty(loc: any): boolean {
        // If there is no key at the location, and it is not reserved, then it's empty
        return !(loc.KeyLocation?.some((kL) => kL.endDate === null) || loc.keyId);
    }

    private getLocationIcon(loc: any): string {
        if (loc.KeyLocation?.some((kL) => kL.endDate === null)) {
            return "icon-picto_cle-partagees";
        } else if (loc.keyId) {
            return "icon-picto_mes-contacts";
        } else {
            return "";
        }
    }

    private getColumnName(val): string {
        return (val >= 26 ? this.getColumnName( Math.floor((val / 26) - 1)) : "") +
            "ABCDEFGHIJKLMNOPQRSTUVWXYZ"[(val % 26)];
    }

    private handleQrCodeLink(event, loc): void {
      if (!loc.qrCode) {
        event.preventDefault();
      }
    }

    private didSizeChange(loc): boolean {
      const originalLocation = this.locations.find((l) => l.id === loc.id);
      return originalLocation.Size?.id !== loc.Size?.id;
    }

    private async fetchAvailableSizes(): Promise<void> {
      await this.$store.dispatch("sizes/getAll", {
        provider: this.connectedBoxProvider,
      }).then((sizes) => {
        this.availableSizes = sizes;
      });
    }

    private changeLocationSize(loc): void {
      const curSizeIdx = this.availableSizes.findIndex((size) => loc.Size?.id === size.id);
      const nextSizeIdx = (curSizeIdx + 1) % this.availableSizes.length;
      loc.Size = this.availableSizes[nextSizeIdx];
      loc.edited = this.didSizeChange(loc);
      this.$forceUpdate();
    }

    private closeDialog(): void {
        this.$emit("close");
    }

    private changeSheet(type: string) {
        if (type === "plus" && this.activeSheet !== this.box.sheets) {
            this.activeSheet += 1;
        } else if (type === "minus" && this.activeSheet !== 1) {
            this.activeSheet -= 1;
        }
    }

    private enterEditSizeMode() {
        this.resetLocations();
        this.isEditSizeMode = true;
    }

    private quitEditSizeMode() {
        this.isEditSizeMode = false;
        this.resetLocations();
    }

    private saveLocationsSizes() {
      this.loadingEditLocationsSize = true;
      const locationsToUpdate = this.displayedLocations
        .filter((l) => l.edited)
        .map((l) => ({
          id: l.id,
          sizeId: l.Size?.id,
        }));
      this.$store.dispatch("boxes/updateLocationsSize", {
        id: this.box.id,
        locations: locationsToUpdate,
      }).then(() => {
        this.$emit("refresh");
        this.$store.commit("alerts/displaySuccess", {
          msg: this.$i18n?.t("actions.saved"),
        });
        this.quitEditSizeMode();
      }).catch((error) => {
        this.$store.commit("alerts/displayHttpError", { error });
      }).finally(() => {
        this.loadingEditLocationsSize = false;
      });
    }
}
